import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Row, Col, Card, Button, Alert } from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'
import { useSelector } from 'react-redux'

import SimpleMantenedorLayout from '../../../components/SimpleMantenedorLayout'
import { useMantenedores } from '../../../context/MantenedoresContext'
import { useCierre } from '../../../hooks/useCierre'
import { CierreTable, CierreFilters, NuevoCierreModal } from '../../../components/Cierre'
import GeneratePdfModal from '../../../components/Cierre/modals/GeneratePdfModal'
import SendEmailModal from '../../../components/Cierre/SendEmailModal'
import { SuccessSwal, ErrorSwal } from '@helpers/swal'

const CierreMensualPage = () => {
    const history = useHistory()
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    // Los submódulos de cierre heredan los permisos del módulo padre "cierre"
    const permisos = userPermissions?.modules?.cierre || {}

    // Estados del modal
    const [showNuevoCierreModal, setShowNuevoCierreModal] = useState(false)
    // Estados locales para modales
    const [showPdfModal, setShowPdfModal] = useState(false)
    const [selectedCierreForPdf, setSelectedCierreForPdf] = useState(null)
    // Estados para modal de envío de correos
    const [showSendEmailModal, setShowSendEmailModal] = useState(false)
    const [selectedCierreForEmail, setSelectedCierreForEmail] = useState(null)

    // Hook de cierre
    const {
        cierres,
        total,
        loading,
        error,
        pageNumber,
        pageSize,
        refreshingCierreId,
        generatingPdfCierreId,
        fechaInicio,
        fechaTermino,
        handleFechaInicioChange,
        handleFechaTerminoChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreateCierre,
        handleRefreshCierre,
        handleGeneratePdf,
        handleRefreshTable,
        // Nuevas funciones para envío de correos
        cierreDetalle,
        loadingCierreDetalle,
        sendingEmails,
        loadCierreDetalle,
        handleSendEmails,
        // Templates de email
        emailTemplates,
        loadingEmailTemplates,
        loadEmailTemplates
    } = useCierre(user, permisos)

    // Calcular total de páginas
    const totalPages = Math.ceil(total / pageSize)

    // Navegar atrás
    const handleGoBack = () => {
        history.push('/mantenedores/cierre')
    }

    // Limpiar filtros
    const handleClearFilters = () => {
        handleFechaInicioChange('')
        handleFechaTerminoChange('')
    }

    // Handler para mostrar modal de confirmación de PDF
    const handleShowPdfModal = cierreId => {
        const cierre = cierres.find(c => c.id === cierreId)
        setSelectedCierreForPdf(cierre)
        setShowPdfModal(true)
    }

    // Handler para generar PDF con confirmación
    const handleConfirmGeneratePdf = async () => {
        if (selectedCierreForPdf) {
            try {
                const result = await handleGeneratePdf(selectedCierreForPdf.id);
                
                if (result.success) {
                    SuccessSwal(
                        'PDF Generado',
                        result.message || 'El PDF se está generando exitosamente'
                    );
                    // Cerrar modal solo si fue exitoso
                    setShowPdfModal(false);
                    setSelectedCierreForPdf(null);
                } else {
                    ErrorSwal(
                        'Error al Generar PDF', 
                        result.error || 'No se pudo generar el PDF'
                    );
                }
            } catch (error) {
                ErrorSwal(
                    'Error al Generar PDF', 
                    'Ocurrió un error inesperado al generar el PDF'
                );
            }
        }
    }

    // Crear nuevo cierre
    const handleNuevoCierre = async cierreData => {
        try {
            const result = await handleCreateCierre(cierreData)

            if (result.success) {
                SuccessSwal(
                    'Cierre Creado',
                    `${result.message || 'El cierre se ha creado exitosamente'}. ${
                        result.data?.tiempo_estimado || 'Procesamiento iniciado'
                    }.`
                )
                setShowNuevoCierreModal(false)
            } else {
                ErrorSwal('Error al Crear Cierre', result.error || 'No se pudo crear el cierre')
            }
        } catch (error) {
            ErrorSwal('Error al Crear Cierre', 'Ocurrió un error inesperado al crear el cierre')
        }
    }

    // Refresh cierre
    const handleRefreshCierreAction = async cierreId => {
        try {
            const result = await handleRefreshCierre(cierreId)

            if (result.success) {
                SuccessSwal('Cierre Actualizado', result.message || 'El cierre se está regenerando exitosamente')
            } else {
                ErrorSwal('Error al Actualizar Cierre', result.error || 'No se pudo actualizar el cierre')
            }
        } catch (error) {
            ErrorSwal('Error al Actualizar Cierre', 'Ocurrió un error inesperado al actualizar el cierre')
        }
    }

    // Handler para mostrar modal de envío de correos
    const handleShowSendEmailModal = async cierreId => {
        setSelectedCierreForEmail(cierreId)
        setShowSendEmailModal(true)
        
        // Cargar detalle del cierre y templates en paralelo
        await Promise.all([
            loadCierreDetalle(cierreId),
            emailTemplates.length === 0 ? loadEmailTemplates() : Promise.resolve()
        ])
    }

    // Handler para enviar correos con confirmación
    const handleConfirmSendEmails = async emailData => {
        if (selectedCierreForEmail) {
            try {
                const result = await handleSendEmails(selectedCierreForEmail, emailData)
                
                if (result.success) {
                    SuccessSwal(
                        'Proceso de Envío Iniciado',
                        `${result.message || 'El proceso de envío de correos ha comenzado'}. ${
                            result.test_mode ? 'Modo de prueba activado.' : ''
                        } El proceso puede tardar unos 5 minutos en completarse.`
                    )
                    setShowSendEmailModal(false)
                    setSelectedCierreForEmail(null)
                } else {
                    ErrorSwal(
                        'Error al Enviar Correos',
                        result.error || 'Ocurrió un error al iniciar el envío de correos'
                    )
                }
            } catch (error) {
                ErrorSwal(
                    'Error al Enviar Correos', 
                    'Ocurrió un error inesperado al enviar correos'
                )
            }
        }
    }

    return (
        <SimpleMantenedorLayout
            mantenedorId="cierre"
            title="Cierre Mensual"
            subtitle="Gestión y generación de cierres mensuales de conductores"
            iconColor="#7b1fa2"
            breadcrumbs={[{ label: 'Cierre', path: '/mantenedores/cierre' }, { label: 'Cierre Mensual' }]}
            showBackButton={false}
            onBackClick={handleGoBack}>
            {/* Encabezado con botón de nuevo cierre */}
            <Row className="mb-4">
                <Col xs={12}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 style={{ color: '#212529', marginBottom: '8px' }}>Historial de Cierres Mensuales</h4>
                            <p style={{ color: '#6c757d', marginBottom: '0' }}>
                                Consulta y gestiona los cierres mensuales generados
                            </p>
                        </div>
                        {(permisos.canCreate || permisos.canEdit) && (
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleRefreshTable}
                                    disabled={loading}
                                    title="Actualizar tabla"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                    <FaIcons.FaSync className={loading ? 'fa-spin' : ''} size={12} />
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={() => setShowNuevoCierreModal(true)}
                                    disabled={loading}>
                                    <FaIcons.FaPlus className="me-2" />
                                    Nuevo Cierre
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Filtros */}
            {/* <CierreFilters
        fechaInicio={fechaInicio}
        fechaTermino={fechaTermino}
        onFechaInicioChange={handleFechaInicioChange}
        onFechaTerminoChange={handleFechaTerminoChange}
        onClearFilters={handleClearFilters}
        loading={loading}
      /> */}

            {/* Error */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <FaIcons.FaExclamationTriangle className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Tabla de cierres */}
            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <CierreTable
                        cierres={cierres}
                        loading={loading}
                        error={error}
                        pagination={{
                            currentPage: pageNumber,
                            totalPages: totalPages,
                            totalItems: total,
                            pageSize: pageSize
                        }}
                        refreshingCierreId={refreshingCierreId}
                        generatingPdfCierreId={generatingPdfCierreId}
                        onPageChange={handlePageChange}
                        onRefresh={handleRefreshCierreAction}
                        onGeneratePdf={handleShowPdfModal}
                        onSendEmails={handleShowSendEmailModal}
                        canCreate={permisos.canCreate || permisos.canEdit}
                        canRefresh={permisos.canEdit}
                        canGeneratePdf={permisos.canEdit}
                        canSendEmail={permisos.canApprove}
                        onShowCreateModal={() => setShowNuevoCierreModal(true)}
                    />
                </Card.Body>
            </Card>

            {/* Información adicional */}
            <Row className="mt-4">
                <Col xs={12}>
                    <Card className="border-0 bg-light">
                        <Card.Body className="p-4">
                            <Row>
                                <Col xs={12}>
                                    <h6 style={{ color: '#495057', marginBottom: '12px' }}>
                                        <FaIcons.FaInfoCircle className="me-2" style={{ color: '#17a2b8' }} />
                                        Información del Cierre Mensual
                                    </h6>
                                    <p style={{ color: '#6c757d', marginBottom: '0', fontSize: '0.9rem' }}>
                                        Los cierres mensuales consolidan todas las entregas, pagos y extras de
                                        conductores en un periodo específico. El procesamiento puede tomar entre 5 y 10
                                        minutos dependiendo del volumen de datos. Los archivos se generan
                                        automáticamente en Google Drive.
                                    </p>
                                    {/* </Col>
                <Col xs={12} md={4} className="d-flex align-items-center justify-content-md-end mt-3 mt-md-0">
                  <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                    <div><strong>Permisos actuales:</strong></div>
                    <div>Vista: {permisos.canView ? '✓' : '✗'}</div>
                    <div>Crear: {permisos.canCreate ? '✓' : '✗'}</div>
                    <div>Editar: {permisos.canEdit ? '✓' : '✗'}</div>
                  </div> */}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de nuevo cierre */}
            <NuevoCierreModal
                show={showNuevoCierreModal}
                onHide={() => setShowNuevoCierreModal(false)}
                onSubmit={handleNuevoCierre}
                loading={loading}
            />

            {/* Modal para generar PDF */}
            <GeneratePdfModal
                show={showPdfModal}
                onHide={() => {
                    // Solo permitir cerrar si no está generando
                    if (!generatingPdfCierreId) {
                        setShowPdfModal(false)
                        setSelectedCierreForPdf(null)
                    }
                }}
                onConfirm={handleConfirmGeneratePdf}
                loading={loading}
                isGenerating={generatingPdfCierreId === selectedCierreForPdf?.id}
                cierreData={selectedCierreForPdf}
            />

            {/* Modal para envío de correos */}
            <SendEmailModal
                show={showSendEmailModal}
                onHide={() => {
                    // Solo permitir cerrar si no está enviando
                    if (!sendingEmails) {
                        setShowSendEmailModal(false)
                        setSelectedCierreForEmail(null)
                    }
                }}
                onSendEmails={handleConfirmSendEmails}
                cierreDetalle={cierreDetalle}
                loading={loadingCierreDetalle}
                sendingEmails={sendingEmails}
                emailTemplates={emailTemplates}
                loadingEmailTemplates={loadingEmailTemplates}
            />
        </SimpleMantenedorLayout>
    )
}

export default CierreMensualPage
